'use client';

import { datadogRum } from '@datadog/browser-rum';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState
} from 'react';
import { DATADOG_EVENTS } from '~/constants/flp/datadogEvents';
import {
  Applicant,
  BodyStyles,
  CreditApplication,
  CreditStatus,
  PricingAggregates,
  TradeInDetails,
  VehicleAggregate
} from '~/contracts/flp';
import { getDownPaymentBuckets } from '~/utilities/flp/getDownPaymentBuckets';
import validateFinancingData from '~/utilities/flp/validateFinancingData';
import { logError } from '~/utilities/loggers';
import { reducer } from './Reducers';

/**
 * Represents a feature within the terms of a deal
 * @template T - The type of data associated with the feature.
 */
type TermsFeature<T> =
  | {
      /* Indicates whether the feature is active and considered in the current user's terms. */
      enabled?: boolean;
      /* Individual error state of feature, if any. */
      error?: string | null;
      /* Data associated with feature. */
      data?: T | null;
    }
  /* Feature is not available for current user. Example, state restrictions for cosigner. */
  | null;

export type FinancingContextType = {
  aggregateMap: Map<BodyStyles, VehicleAggregate>;
  aggregates:
    | (Omit<PricingAggregates, 'financeInfo' | 'tradeInfo'> & {
        downPaymentDropdownOptions: number[];
        monthlyPaymentDropdownOptions: number[];
      })
    | null;
  applicant: Applicant | null;
  coSigner: TermsFeature<{
    firstName: string;
    lastName: string;
  }>;
  creditStatus: CreditStatus | null;
  financeTerms:
    | (PricingAggregates['financeInfo'] & {
        expiration: string;
        error?: string;
      })
    | null;
  inventory: number | null;
  isInitialized: boolean;
  isLoading: boolean;
  tradeIn: TermsFeature<TradeInDetails>;
  setCoSigner: Dispatch<SetStateAction<FinancingContextType['coSigner']>>;
  setIsInitialized: Dispatch<SetStateAction<boolean>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setRefreshedData: (
    caller: string,
    tradeInApplied: boolean,
    creditApplication: CreditApplication,
    pricingAggregates?: PricingAggregates
  ) => void;
};

export const FinancingContext = createContext<FinancingContextType>({
  aggregateMap: new Map(),
  aggregates: null,
  applicant: null,
  coSigner: null,
  creditStatus: null,
  financeTerms: null,
  inventory: null,
  isInitialized: false,
  isLoading: true,
  tradeIn: null,
  setCoSigner() {},
  setIsInitialized() {},
  setIsLoading() {},
  setRefreshedData() {}
});

export function FinancingContextProvider({
  children
}: React.PropsWithChildren) {
  const [aggregates, setAggregates] =
    useState<FinancingContextType['aggregates']>(null);
  const [applicant, setApplicant] = useState<Applicant | null>(null);
  const [coSigner, setCoSigner] =
    useState<FinancingContextType['coSigner']>(null);
  const [creditStatus, setCreditStatus] =
    useState<FinancingContextType['creditStatus']>(null);
  const [financeTerms, setFinanceTerms] =
    useState<FinancingContextType['financeTerms']>(null);
  const [inventory, setInventory] =
    useState<FinancingContextType['inventory']>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tradeIn, setTradeIn] = useState<FinancingContextType['tradeIn']>(null);

  const setRefreshedData = (
    caller: string,
    tradeInApplied: boolean,
    creditApplication: CreditApplication,
    pricingAggregates?: PricingAggregates
  ) => {
    // Only validate data we're actually about to render — an expired or
    // missing softpull renders Marketing regardless of shape, so it isn't
    // a real error.
    if (creditApplication?.creditStatus?.hasValidSoftPull) {
      validateFinancingData(creditApplication, pricingAggregates);
    }

    // Expired softpull is handled in the SoftpullStatus component
    setCreditStatus(creditApplication?.creditStatus ?? null);
    setApplicant(creditApplication?.applicant);
    setCoSigner(reducer.cosigner(creditApplication));

    datadogRum.addAction(DATADOG_EVENTS.API.CREDIT_APPLICATION_UPDATED, {
      caller,
      creditStatus: creditApplication?.creditStatus,
      hasCoApplicant: Boolean(creditApplication?.hasCoApplicant),
      hasTradeIn: Boolean(creditApplication?.hasTradeIn),
      // properties used for pricing aggregates request
      applyTradeIn: tradeInApplied,
      downPaymentBreakpoints: getDownPaymentBuckets(
        creditApplication?.financingPolicy?.minMinDown ?? 0
      ),
      zipCode: creditApplication?.applicant?.address?.zip
    });

    if (!pricingAggregates) {
      datadogRum.addAction(DATADOG_EVENTS.API.CALLER_MISSING_DATA, {
        missingProperty: 'pricingAggregates',
        caller: 'setRefreshedData'
      });
      logError('Failed to retrieve pricing aggregates.');

      return;
    }

    setAggregates(reducer.aggregates(pricingAggregates));
    setFinanceTerms(reducer.financeterms(pricingAggregates, creditApplication));
    setTradeIn(reducer.tradein(pricingAggregates));
    setInventory(pricingAggregates.totalMatchingInventory);
  };

  const memo = useMemo(() => {
    return {
      aggregateMap: new Map(
        (aggregates?.bodyStyles ?? []).map(a => [a.name, a])
      ),
      aggregates,
      applicant,
      coSigner,
      creditStatus,
      financeTerms,
      inventory,
      isInitialized,
      isLoading,
      tradeIn,
      setCoSigner,
      setIsInitialized,
      setIsLoading,
      setRefreshedData
    };
  }, [
    aggregates,
    applicant,
    coSigner,
    creditStatus,
    financeTerms,
    inventory,
    isInitialized,
    isLoading,
    tradeIn
  ]);

  return (
    <FinancingContext.Provider value={memo}>
      {children}
    </FinancingContext.Provider>
  );
}

export function useFinancingContext() {
  const ctx = useContext(FinancingContext);
  if (ctx === undefined) {
    throw new Error(
      '`useFinancingContext` must be used within a FinancingContextProvider'
    );
  }

  return ctx;
}
